import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { LocationOptionsComponent } from '@locations';
import { AvsDateRangeIds } from '@ui';

import { BaseWidgetDeviceConfig, StatsSupportedTypes, WidgetDeviceChart } from '../../../models';
import { DeviceDataPointSelectorComponent, DeviceSelectorComponent } from '../../selectors';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DeviceDataPointSelectorComponent,
    DeviceSelectorComponent,
    LocationOptionsComponent,
    MatFormFieldModule,
    MatSelectModule,
    CommonModule,
    MatIconModule,
  ],
  selector: 'avs-room-state-options',
  templateUrl: './room-state-options.component.html',
})
export class RoomStateOptionsComponent {
  changed = output<Partial<WidgetDeviceChart>>();
  config = input<BaseWidgetDeviceConfig<string>>();

  protected dataTypes = [
    {
      title: 'Power',
      value: StatsSupportedTypes.POWER,
    },
    {
      title: 'Temperature',
      value: StatsSupportedTypes.THERMOSTAT,
    },
  ];

  protected locationConfig = computed(() => {
    if (this.config()?.locationId) {
      return {
        floorId: this.config()!.floorId,
        locationId: this.config()!.locationId,
        roomId: this.config()!.roomId,
      };
    }

    return null;
  });

  protected eAvsDateRangeIds = AvsDateRangeIds;
  protected form: FormGroup;

  private fb = inject(FormBuilder);

  constructor() {
    this.form = this.fb.group({
      datapoints: this.fb.group({
        co2: ['', Validators.required],
        humidity: ['', Validators.required],
        people: ['', Validators.required],
        pm10: ['', Validators.required],
        pm25: ['', Validators.required],
        temperature: ['', Validators.required],
        tvoc: ['', Validators.required],
      }),
      devices: this.fb.group({
        airMonitor: ['', Validators.required],
        camera: ['', Validators.required],
      }),
      floorId: ['', Validators.required],
      locationId: ['', Validators.required],
      roomId: ['', Validators.required],
    });

    const initialEffect = effect(() => {
      this.form.patchValue(this.config() ?? {});
      initialEffect.destroy();
    }, { manualCleanup: true });
  }

  protected patchForm(value: object) {
    this.form.patchValue(value);
    this.changed.emit(this.form.valid ? this.form.value : null);
  }

  protected patchLocationForm(form: Partial<WidgetDeviceChart> | null) {
    this.form.reset();
    this.patchForm(form ?? {});
  }
}
